import { callFunction } from "./functions-client";

// Dispatch — shipment tracking for awarded loads (LOS Phase 11).
export const SHIPMENT_STATUSES = ["dispatched", "picked_up", "in_transit", "delivered", "issue"] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

export interface Shipment {
  id: string;
  status: ShipmentStatus;
  notes?: string | null;
  updated_at: string;
  load_id: string;
  as_poster: boolean;
  origin: string;
  destination: string;
  equipment_type: string;
  pickup_date: string | null;
  // present on `get`
  delivery_date?: string | null;
  rate?: number | null;
  picked_up_at?: string | null;
  delivered_at?: string | null;
}

export const createShipment = (load_id: string) =>
  callFunction<{ shipment: Shipment }>("dispatch/create", { body: { load_id } });

export const listShipments = () =>
  callFunction<{ shipments: Shipment[] }>("dispatch/board", { method: "GET" });

export const getShipment = (id: string) =>
  callFunction<{ shipment: Shipment }>(`dispatch/get?id=${encodeURIComponent(id)}`, { method: "GET" });

export const updateShipmentStatus = (input: { id: string; status: ShipmentStatus; notes?: string }) =>
  callFunction<{ ok: boolean; status: ShipmentStatus }>("dispatch/update-status", { body: input });

// Live location tracking (LOS Phase 20 — Mobile). The carrier streams breadcrumbs; the
// poster + carrier track a shipment, and the fleet view lists active shipments' positions.
export interface GeoPoint { lat: number; lng: number; speed?: number | null; heading?: number | null; recorded_at?: string }

export interface TrackShipment {
  id: string;
  status: ShipmentStatus;
  load_id: string;
  origin: string;
  destination: string;
  origin_lat: number | null;
  origin_lng: number | null;
  dest_lat: number | null;
  dest_lng: number | null;
}

export interface FleetShipment {
  id: string;
  status: ShipmentStatus;
  load_id: string;
  origin: string;
  destination: string;
  as_poster: boolean;
  lat: number | null;
  lng: number | null;
  recorded_at: string | null;
}

export const updateLocation = (input: { shipment_id: string; lat: number; lng: number; speed?: number; heading?: number }) =>
  callFunction<{ ok: boolean }>("dispatch/location-update", { body: input });

export interface GeofenceEvent { event: "enter" | "exit"; recorded_at: string; name: string; kind: string }

export const trackShipment = (id: string) =>
  callFunction<{ shipment: TrackShipment; current: GeoPoint | null; trail: GeoPoint[]; eta_minutes: number | null; geofence_events: GeofenceEvent[] }>(
    `dispatch/track?id=${encodeURIComponent(id)}`, { method: "GET" });

export const fleetLocations = () =>
  callFunction<{ shipments: FleetShipment[] }>("dispatch/fleet", { method: "GET" });

// F1 telematics: geofences (arrival/departure) + a public customer tracking link.
export interface Geofence { id: string; name: string; kind: string; lat: number; lng: number; radius_m: number; created_at?: string }

export const createGeofence = (input: { name: string; lat: number; lng: number; radius_m?: number; kind?: string }) =>
  callFunction<{ geofence: Geofence }>("dispatch/geofence-create", { body: input });

export const listGeofences = () =>
  callFunction<{ geofences: Geofence[] }>("dispatch/geofences", { method: "GET" });

export const deleteGeofence = (id: string) =>
  callFunction<{ ok: boolean }>("dispatch/geofence-delete", { body: { id } });

export const trackingLink = (shipment_id: string) =>
  callFunction<{ token: string; path: string }>("dispatch/tracking-link", { body: { shipment_id } });

export interface PublicTrack {
  origin: string;
  destination: string;
  status: ShipmentStatus;
  current: { lat: number; lng: number; speed?: number | null; recorded_at?: string } | null;
  eta_minutes: number | null;
}

// Unauthenticated (customer link). No bearer is sent when logged out; the route is public.
export const publicTrack = (token: string) =>
  callFunction<PublicTrack>(`dispatch/public-track?token=${encodeURIComponent(token)}`, { method: "GET" });
